/**
 * Tickets and Products are seperate API points however, so CompoundTicket is a
 * proxy for managing that complexity.
 *
 * @param {AngularModule} ngModule A module that can be extended with factories
 */
export default function(ngModule) {
    ngModule.factory('CompoundTicket', function(Ticket, Product, ProductGroup, $q, $translate, $timeout, EventDate) {
        function CompoundTicket(attributes) {
            if (attributes) {
                _.assign(this, attributes);
                this.regular = this.$isRegular();
            } else
                this.regular = true;

            this.ticket = this.ticket || Ticket.new({
                increment: 1,
                status_overrule: 'auto',
                available_stock: 0,
                availability_margin: 15,
                min_orderable_amount_per_order: 1,
                max_orderable_amount_per_order: 20,
                percentage_service_costs_in_ticket: 0
            });
            this.product = this.product || Product.new({
                name: 'Product for ',
                type: 'normal',
                price: 100,
                vat_percentage: 21
            });
            this.groups = this.groups || [];

            /* We differentiate access- and other products so we can
             * detect when user doesn't attach any products with dates
             *
             * Without such accessProducts, there's no explicit link between
             * ticket and event. So we attach a faux access product behind
             * the curtains
             */
            this.products = this.products || [];
            this.eventDates = this.eventDates || [];
            this.accessProducts = this.accessProducts || [];
            this.metaData = this.metaData || [];

            Object.defineProperty(this, '$errors', {
                value: new ErrorObject(this)
            });
        }
        CompoundTicket.modelName = 'CompoundTicket';

        /**
         * Check if this is a ticket that follows a standard workflow
         *
         * Standard workflow has 1 ticketProduct for every ticket and multiple
         * accessProducts for every eventdate.
         *
         * @returns {Boolean} True if it follows this default workflow
         */
        CompoundTicket.prototype.$isRegular = function() {
            if(!this.product || !this.ticket) return false;
            return this.product.description === this.ticket.guid &&
                   _.get(this.product,'name', '').indexOf('Product for ') === 0;
        };

        /**
         * Attach proxy date to create explicit link between ticket & event
         *
         * If CompoundTicket is created without attaching any dates, the ticket
         * can't be connected to an event and is essentially an orphan in the DB
         *
         * So we have this convenience method to attach a hidden date.
         *
         * @param {Event} event Instance of `common.models.Event`
         * @return {Promise} Resolves when done
         */
        CompoundTicket.prototype.$attachProxyDate = function(event) {
            if(this.accessProducts.length)
                return $q.reject('Ticket has access products, no need for proxy date');
            // Ensure dates are in cache
            return event.$queryEventDate().then((realDates) => {
                // $queryEventDate filters out proxy date, so lookup through cache
                let dates = EventDate.cached.where({
                    event_id: event.guid,
                    name: 'Proxy date'
                });
                if(dates.length) {
                    if(this.guid)
                        return this.$attachEventDate(dates[0]);
                    this.eventDates.push(dates[0]);
                    return $q.resolve();
                }

                if(realDates.length === 0)
                    return $q.reject('We dont have a real date to copy information from');

                // Proxy date doesn't exist yet, so create one
                let date = EventDate.new({
                    event_id: event.guid,
                    location_id: realDates[0].location_id,
                    name: 'Proxy date',
                    // if this code is still running in 2099, I'll eat my socks
                    start: '2099-01-01T00:00:00+00',
                    end: '2099-01-01T23:59:59+00'
                });
                return date.$save().then(() => {
                    if(this.guid)
                        return this.$attachEventDate(date);
                    this.eventDates.push(date);
                    return $q.resolve();
                });
            });
        };

        /**
         * Reconstruct the CompoundTicket from an array of regular tickets
         *
         * @param {Ticket[]} tickets Array of tickets
         * @returns {CompoundTicket[]} Promise that resolves with array of CompoundTicket instances
         */
        CompoundTicket.constructFromTickets = function(tickets) {
            if (!_.isArray(tickets)) {
                if (!_.isObject(tickets))
                    return $q.resolve([]);
                tickets = [tickets];
            }
            return $q.all(_.map(tickets, ticket => {
                var compound = {
                    ticket: ticket,
                    eventDates: []
                };
                var productPromise = ticket.$queryProduct().then(products => {
                    compound.products = products;
                    compound.accessProducts = _.filter(products, p => p.isAccessProduct());
                    let promises = _.map(compound.accessProducts, p => {
                        return EventDate.get({ guid: p.description }).then(date => {
                            compound.eventDates.push(date);
                            return $q.resolve();
                        });
                    });
                    let mainProductIndex = _.findIndex(products, p => p.isTicketProduct(ticket.guid));
                    if(mainProductIndex >= 0) {
                        compound.product = products[mainProductIndex];
                        promises.push(compound.product.$queryMetaData().then(m => compound.metaData = m));
                    }
                    return $q.all(promises);
                });
                var groupPromise = ticket.$queryProductGroup().then(groups => compound.groups = groups);
                return $q.all([productPromise, groupPromise]).then(() => new CompoundTicket(compound));
            }));
        };

        CompoundTicket.primaryKey = 'guid';

        /**
         * Sync the name and description between ticket and main product
         */
        CompoundTicket.prototype.$syncProperties = function() {
            if(!this.product) return;
            this.product.name = 'Product for ' + this.ticket.name;
            this.product.description = this.ticket.guid;
        };

        /**
         * Create the ticket and main product with their relations on the API
         *
         * @returns {CompoundTicket} Promise that resolves with CompoundTicket
         */
        CompoundTicket.prototype.$create = function() {
            return this.ticket.$save().then(() => {
                this.$syncProperties();
                return this.product ? this.product.$save().then(() => this.$attachProduct(this.product)) : $q.resolve();
            }).then(() => {
                var promises = _.map(this.metaData, m => this.$attachMetaData(m));
                promises.push(_.reduce(this.products, (promise, product) => {
                    return promise.then(() => {
                        return this.$attachProduct(product);
                    });
                }, $q.resolve()));

                return $q.all(promises).then(() => {
                    this.products.push(this.product);
                    return _.reduce(this.eventDates, (promise, date) => {
                        return promise.then(() => {
                            return this.$attachEventDate(date);
                        });
                    }, $q.resolve());
                }).then(() => {
                    this.groups.push(ProductGroup.new({
                        min_bound: 1 + this.accessProducts.length,
                        max_bound: 1 + this.accessProducts.length,
                        uniqueness: 1,
                        products: [this.product.guid].concat(_.map(this.accessProducts, 'guid')),
                        ticket_id: this.ticket.guid
                    }));

                    let groupPromises = _.map(this.groups, g => {
                        g.ticket_id = this.ticket.guid;
                        return g.$save();
                    });
                    return $q.all(groupPromises);
                });
            });
        };

        /**
         * Persist changes to an existing CompoundModel to the API
         *
         * @returns {CompoundTicket} Promise that resolves with CompoundTicket
         */
        CompoundTicket.prototype.$update = function() {
            this.$syncProperties();
            var promises = [this.ticket.$save()];
            if (this.product)
                promises.push(this.product.$save());
            promises = promises.concat(_.map(this.groups, group => {
                group.ticket_id = this.ticket.guid;
                return group.$save();
            }));
            return $q.all(promises);
        };

        /**
         * Proxy method that chooses `$create` or `$update` approprirately
         *
         * @returns {CompoundTicket} Promise that resolves with CompoundTicket
         */
        CompoundTicket.prototype.$save = function() {
            if (this.guid)
                return this.$update();
            return this.$create();
        };

        /**
         * Delete ticket and product that make up this CompoundTicket
         *
         * @returns {Promise} Empty promise
         */
        CompoundTicket.prototype.$delete = function() {
            return this.ticket.$delete()
                .then(() => {
                    if (this.product && this.product.guid)
                        return this.product.$delete();
                    return $q.resolve();
                });
        };

        /**
         * Proxy method for Ticket#$detachProduct
         * @param {Product} product Instance to attach
         * @return {Promise} Empty promise
         */
        CompoundTicket.prototype.$detachProduct = function(product) {
            return this.ticket.$detachProduct(product).then(() => {
                let promises = _.filter(this.groups, group => {
                    let index = _.indexOf(group.products, product.guid);
                    if(index < 0)
                        return $q.resolve();
                    if(group.isRequiredGroup()) {
                        group.min_bound--;
                        group.max_bound--;
                    }
                    group.products.splice(index, 1);
                    return group.$save();
                });
            });
        };

        /**
         * Proxy method for Ticket#$attachProduct
         * @param {Product} product Instance to attach
         * @return {Promise} Empty promise
         */
        CompoundTicket.prototype.$attachProduct = function(product) {
            return this.ticket.$attachProduct(product);
        };

        /**
         * Proxy method for Ticket#$queryProduct
         * @return {Promise} Empty promise
         */
        CompoundTicket.prototype.$queryProduct = function() {
            // If we've already got products, return them so we maintain object references like
            // this.product === this.products[0]
            if(this.products) return $q.resolve(this.products);
            return this.ticket.$queryProduct();
        };

        /**
         * Proxy method for Product#$detachMetaData
         * @param {MetaData} meta Instance to attach
         * @return {Promise} Empty promise
         */
        CompoundTicket.prototype.$detachMetaData = function(meta) {
            if (!this.product.guid) return $q.resolve([]);
            return this.product.$detachMetaData(meta);
        };

        /**
         * Proxy method for Product#$attachMetaData
         * @param {MetaData} meta Instance to attach
         * @return {Promise} Empty promise
         */
        CompoundTicket.prototype.$attachMetaData = function(meta) {
            if (!this.product.guid) return $q.resolve([]);
            return this.product.$attachMetaData(meta);
        };

        /**
         * Proxy method for Product#$queryMetaData
         * @return {Promise} Empty promise
         */
        CompoundTicket.prototype.$queryMetaData = function() {
            if (this.product.guid)
                return this.product.$queryMetaData();
            return $q.resolve([]);
        };

        /**
         * Method that indirectly attaches an eventDate to this ticket
         *
         * It actually finds or creates a hidden product and attaches
         * that instead
         *
         * @param {EventDate} eventDate The instance you wish to attach
         * @returns {Promise} Resolves when done
         */
        CompoundTicket.prototype.$attachEventDate = function(eventDate) {
            return Product.findForDate(eventDate).catch(error => {
                if(error.message !== 'Not Found')
                    return $q.reject(error);
                return Product.createForDate(eventDate);
            }).then(product => {
                if(_.find(this.accessProducts, { guid: product.guid }))
                    return $q.resolve(this);
                return this.$attachProduct(product).then(() => {
                    let group = _.find(this.groups, { min_bound: this.accessProducts.length + 1, max_bound: this.accessProducts.length + 1, uniqueness: 1 });
                    this.accessProducts.push(product);
                    this.products.push(product);
                    if(group) {
                        group.min_bound++;
                        group.max_bound++;
                        group.products.push(product.guid);
                        group.$save();
                    }
                    return product;
                });
            });
        };

        /**
         * Method that indirectly detaches an eventDate from this ticket
         *
         * It actually finds the hidden product and detaches that instead
         *
         * @param {EventDate} eventDate The instance you wish to attach
         * @returns {Promise} Resolves when done
         */
        CompoundTicket.prototype.$detachEventDate = function(eventDate) {
            return Product.findForDate(eventDate)
                .then(product => {
                    // Was never attached
                    if(!_.find(this.accessProducts, { guid: product.guid }))
                        return $q.resolve(this);
                    let promises = [];

                    // Detach from groups
                    _.forEach(this.groups, group => {
                        let index = group.products.indexOf(product.guid);
                        if(index < 0)
                            return;
                        if(group.isRequiredGroup()) {
                            group.min_bound--;
                            group.max_bound--;
                        }
                        group.products.splice(index, 1);
                        let promise = group.$save();
                        promises.push(promise);
                    });

                    return $q.all(promises)
                        .then(() => this.$detachProduct(product))
                        .then(() => {
                            _.remove(this.accessProducts, { guid: product.guid } );
                            _.remove(this.products, { guid: product.guid });
                        });
                });
        };

        /**
         * Method that indirectly queries EventDate
         *
         * @returns {EventDate[]} Resolves when done
         */
        CompoundTicket.prototype.$queryEventDate = function() {
            let dates = [];
            let promises = _.map(this.accessProducts, product => {
                return EventDate.get({ guid: product.description }).then(date => {
                    dates.push(date);
                });
            });
            return $q.all(promises).then(() => {
                return _.uniqBy(dates, 'guid');
            });
        };

        CompoundTicket.prototype.toString = function() {
            return _.get(this.ticket, 'name', 'ticket');
        };

        /**
         * Error object that mimicks BaseClass.Errorable
         * @constructor
         * @param {CompoundTicket} parent Parent object
         */
        function ErrorObject(parent) {
            Object.defineProperty(this, 'parent', {
                value: parent
            });
        }
        ErrorObject.prototype.clear = function(fieldName) {
            _.each(_.keys(this), key => {
                delete this[key];
            });
            this.parent.ticket.$errors.clear(fieldName);
            this.parent.product.$errors.clear(fieldName);
        };

        Object.defineProperties(CompoundTicket.prototype, {
            /** Proxy property that returns `ticket#guid` */
            guid: {
                get: function() { return this.ticket.guid; },
                enumerable: false
            },
            /**
             * Is this configuration of CompoundTicket valid?
             *
             * @returns {Boolean} True if invalid
             */
            $invalid: {
                get: function() {
                    if (this.guid)
                        return this.ticket.$invalid && (!this.product || this.product.$invalid);
                    this.$syncProperties();
                    let invalid = false;
                    invalid = !this.ticket.validate() || invalid;
                    invalid = this.product ? !this.product.validate() || invalid : invalid;
                    for (let i = 0; i < this.groups.length; i++)
                        invalid = !this.groups[i].validate() || invalid;
                    return invalid;
                },
                enumerable: false,
                configurable: false
            },
            /**
             * Is this configuration of CompoundTicket valid?
             *
             * @returns {Boolean} True if valid
             */
            $valid: {
                get: function() {
                    return !this.$invalid;
                },
                enumerable: false,
                configurable: false
            },
            validate: {
                enumerable: false,
                configurable: false,
                value: function(fieldName) {
                    if(this.product.hasOwnProperty(fieldName))
                        this.product.validate(fieldName);
                    if(this.ticket.hasOwnProperty(fieldName))
                        this.ticket.validate(fieldName);
                }
            }
        });
        return CompoundTicket;
    });
}
